import type { ParseKeys } from 'i18next'
import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { CART_ACCESS } from '@/entities/cart'
import { CATALOG_GLOBAL_ACCESS } from '@/entities/catalog-search'
import { CLIENT_DEBTS_ACCESS, CLIENT_STATS_ACCESS, CLIENTS_ACCESS } from '@/entities/client'
import { EXCHANGE_ACCESS } from '@/entities/exchange'
import { MANAGER_CARTS_ACCESS } from '@/entities/manager-cart'
import { NOTIFICATIONS_ACCESS } from '@/entities/notification'
import { ORDER_CATALOG_ACCESS } from '@/entities/order-catalog'
import { ORDER_CREATE_ACCESS, ORDERS_ACCESS } from '@/entities/order'
import { PC_BUILD_ACCESS, PC_BUILD_CATALOG_ACCESS } from '@/entities/pc-build'
import { PRICES_ACCESS } from '@/entities/price'
import { CATALOG_ACCESS } from '@/entities/product'
import { REPORTS_ACCESS } from '@/entities/report'
import { SERIALS_ACCESS } from '@/entities/serial'
import { SETTLEMENTS_ACCESS } from '@/entities/settlement'
import { SHIPMENTS_ACCESS } from '@/entities/shipment'
import { SUPPLIER_PRICES_ACCESS } from '@/entities/supplier-price'
import { CLIENT_ROUTE_PARAM, type AccessPair, type ClientProfileTab } from '@/shared/lib'
import type { IconName } from '@/shared/ui/Icon'

/*
 * Каждая страница — отдельный чанк. React.lazy, а не route.lazy: граница ожидания кабинета
 * живёт внутри каркаса. Гард рендерит страницу только при доступе — чанк закрытой страницы
 * не скачивается.
 */
const HomePage = lazy(() => import('@/pages/home').then((m) => ({ default: m.HomePage })))
const ClientsPage = lazy(() => import('@/pages/clients').then((m) => ({ default: m.ClientsPage })))
const ClientProfilePage = lazy(() =>
  import('@/pages/client-profile').then((m) => ({ default: m.ClientProfilePage })),
)
const ClientOverviewPage = lazy(() =>
  import('@/pages/client-profile').then((m) => ({ default: m.ClientOverviewPage })),
)
const ClientDebtsPage = lazy(() =>
  import('@/pages/client-debts').then((m) => ({ default: m.ClientDebtsPage })),
)
const StatsPage = lazy(() => import('@/pages/stats').then((m) => ({ default: m.StatsPage })))
const ShipmentsPage = lazy(() =>
  import('@/pages/shipments').then((m) => ({ default: m.ShipmentsPage })),
)
const ShipmentDetailsPage = lazy(() =>
  import('@/pages/shipment-details').then((m) => ({ default: m.ShipmentDetailsPage })),
)
const SettlementsPage = lazy(() =>
  import('@/pages/settlements').then((m) => ({ default: m.SettlementsPage })),
)
const ManagerCartsPage = lazy(() =>
  import('@/pages/manager-carts').then((m) => ({ default: m.ManagerCartsPage })),
)
const ManagerCartPage = lazy(() =>
  import('@/pages/manager-cart').then((m) => ({ default: m.ManagerCartPage })),
)
const CatalogPage = lazy(() => import('@/pages/catalog').then((m) => ({ default: m.CatalogPage })))
const OrderCatalogPage = lazy(() =>
  import('@/pages/order-catalog').then((m) => ({ default: m.OrderCatalogPage })),
)
const PcBuildListPage = lazy(() =>
  import('@/pages/pc-build').then((m) => ({ default: m.PcBuildListPage })),
)
const PcBuildCatalogPage = lazy(() =>
  import('@/pages/pc-build-catalog').then((m) => ({ default: m.PcBuildCatalogPage })),
)
const SearchGlobalPage = lazy(() =>
  import('@/pages/search-global').then((m) => ({ default: m.SearchGlobalPage })),
)
const SearchSuppliersPage = lazy(() =>
  import('@/pages/search-suppliers').then((m) => ({ default: m.SearchSuppliersPage })),
)
const SerialsPage = lazy(() => import('@/pages/serials').then((m) => ({ default: m.SerialsPage })))
const PricesPage = lazy(() => import('@/pages/prices').then((m) => ({ default: m.PricesPage })))
const ReportsPage = lazy(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsPage })),
)
const NotificationsPage = lazy(() =>
  import('@/pages/notifications').then((m) => ({ default: m.NotificationsPage })),
)
const ExchangePage = lazy(() =>
  import('@/pages/exchange').then((m) => ({ default: m.ExchangePage })),
)
const ReservesInvoicesPage = lazy(() =>
  import('@/pages/reserves-invoices').then((m) => ({ default: m.ReservesInvoicesPage })),
)
const CartPage = lazy(() => import('@/pages/cart').then((m) => ({ default: m.CartPage })))
const CheckoutPage = lazy(() =>
  import('@/pages/checkout').then((m) => ({ default: m.CheckoutPage })),
)

/**
 * Вкладка профиля клиента — вложенный маршрут `/clients/:client/<path>`. Своя пара прав — свой
 * гард: клиента из пути страница получает через `useActingMode`.
 */
export type RouteChild = {
  id: string
  /** Нет — обзор, корень профиля (`index`). */
  path?: ClientProfileTab | `${ClientProfileTab}/:${string}`
  access: AccessPair | null
  Page: LazyExoticComponent<ComponentType>
}

export type RouteEntry = {
  /** Ключ пункта меню и тестов. */
  id: string
  path: `/${string}`
  /**
   * Пара прав ручки страницы (`docs/roles/04-pages-by-role.md` §1). `null` — любой вошедший.
   * Режим `none` — «Нет доступа» на этом же адресе; `needs-client` страницу не закрывает.
   */
  access: AccessPair | null
  /** Пункт меню. Порядок меню — порядок реестра. Нет — страница вне меню. */
  nav?: { labelKey: ParseKeys; icon: IconName }
  Page: LazyExoticComponent<ComponentType>
  /** Вложенные страницы: `Page` — раскладка с `<Outlet />`, вкладки открываются внутри неё. */
  children?: readonly RouteChild[]
}

/**
 * Реестр кабинета: одна запись на маршрут. Из него строятся маршруты, гард и меню
 * (`docs/roles/03-frontend-architecture.md` §6). Адрес не зависит от роли: состав меню и
 * доступ задают права пары.
 *
 * Разделы «от имени клиента» (резервы и счета менеджера, отгрузки, взаиморасчёты, корзины и
 * каталог клиента) живут вкладками профиля `/clients/:client/…`, а не пунктами меню.
 */
export const CABINET_ROUTES: readonly RouteEntry[] = [
  {
    id: 'home',
    path: '/',
    access: null,
    nav: { labelKey: 'nav.home', icon: 'home' },
    Page: HomePage,
  },
  {
    id: 'clients',
    path: '/clients',
    access: CLIENTS_ACCESS,
    nav: { labelKey: 'nav.clients', icon: 'users' },
    Page: ClientsPage,
  },
  {
    id: 'client-debts',
    path: '/clients/debts',
    access: CLIENT_DEBTS_ACCESS,
    nav: { labelKey: 'nav.debts', icon: 'scale' },
    Page: ClientDebtsPage,
  },
  {
    id: 'stats',
    path: '/stats',
    access: CLIENT_STATS_ACCESS,
    nav: { labelKey: 'nav.stats', icon: 'chart' },
    Page: StatsPage,
  },
  {
    id: 'catalog',
    path: '/catalog',
    access: CATALOG_ACCESS,
    nav: { labelKey: 'nav.catalog', icon: 'grid' },
    Page: CatalogPage,
  },
  {
    id: 'order-catalog',
    path: '/order-catalog',
    access: ORDER_CATALOG_ACCESS,
    nav: { labelKey: 'nav.orderCatalog', icon: 'box' },
    Page: OrderCatalogPage,
  },
  {
    id: 'pc-build',
    path: '/pc-build',
    access: PC_BUILD_ACCESS,
    nav: { labelKey: 'nav.pcBuild', icon: 'cpu' },
    Page: PcBuildListPage,
  },
  {
    id: 'search-global',
    path: '/search/global',
    access: CATALOG_GLOBAL_ACCESS,
    nav: { labelKey: 'nav.searchGlobal', icon: 'search' },
    Page: SearchGlobalPage,
  },
  {
    id: 'search-suppliers',
    path: '/search/suppliers',
    access: SUPPLIER_PRICES_ACCESS,
    nav: { labelKey: 'nav.supplierPrices', icon: 'box' },
    Page: SearchSuppliersPage,
  },
  {
    id: 'carts',
    path: '/carts',
    access: MANAGER_CARTS_ACCESS,
    nav: { labelKey: 'nav.carts', icon: 'cart' },
    Page: ManagerCartsPage,
  },
  {
    id: 'orders',
    path: '/orders',
    access: ORDERS_ACCESS,
    nav: { labelKey: 'nav.orders', icon: 'doc' },
    Page: ReservesInvoicesPage,
  },
  {
    id: 'serials',
    path: '/serials',
    access: SERIALS_ACCESS,
    nav: { labelKey: 'nav.serials', icon: 'shield' },
    Page: SerialsPage,
  },
  {
    id: 'prices',
    path: '/prices',
    access: PRICES_ACCESS,
    nav: { labelKey: 'nav.prices', icon: 'tag' },
    Page: PricesPage,
  },
  {
    id: 'reports',
    path: '/reports',
    access: REPORTS_ACCESS,
    nav: { labelKey: 'nav.reports', icon: 'folder' },
    Page: ReportsPage,
  },
  {
    id: 'notifications',
    path: '/notifications',
    access: NOTIFICATIONS_ACCESS,
    nav: { labelKey: 'nav.notifications', icon: 'bell' },
    Page: NotificationsPage,
  },
  {
    id: 'exchange',
    path: '/exchange',
    access: EXCHANGE_ACCESS,
    nav: { labelKey: 'nav.exchange', icon: 'swap' },
    Page: ExchangePage,
  },
  {
    id: 'client',
    path: `/clients/:${CLIENT_ROUTE_PARAM}`,
    access: CLIENTS_ACCESS,
    Page: ClientProfilePage,
    children: [
      { id: 'client-overview', access: null, Page: ClientOverviewPage },
      { id: 'client-orders', path: 'orders', access: ORDERS_ACCESS, Page: ReservesInvoicesPage },
      { id: 'client-shipments', path: 'shipments', access: SHIPMENTS_ACCESS, Page: ShipmentsPage },
      {
        id: 'client-shipment',
        path: 'shipments/:doc',
        access: SHIPMENTS_ACCESS,
        Page: ShipmentDetailsPage,
      },
      {
        id: 'client-settlements',
        path: 'settlements',
        access: SETTLEMENTS_ACCESS,
        Page: SettlementsPage,
      },
      { id: 'client-reports', path: 'reports', access: REPORTS_ACCESS, Page: ReportsPage },
      { id: 'client-stats', path: 'stats', access: CLIENT_STATS_ACCESS, Page: StatsPage },
      { id: 'client-carts', path: 'carts', access: MANAGER_CARTS_ACCESS, Page: ManagerCartsPage },
      { id: 'client-catalog', path: 'catalog', access: CATALOG_ACCESS, Page: CatalogPage },
      {
        id: 'client-order-catalog',
        path: 'order-catalog',
        access: ORDER_CATALOG_ACCESS,
        Page: OrderCatalogPage,
      },
      { id: 'client-pc-build', path: 'pc-build', access: PC_BUILD_ACCESS, Page: PcBuildListPage },
    ],
  },
  { id: 'manager-cart', path: '/carts/:id', access: MANAGER_CARTS_ACCESS, Page: ManagerCartPage },
  /* Каталог набора сборки: закрыт правом ручки сборки, не правом списка; `?client=` — глазами клиента */
  { id: 'pc-build-catalog', path: '/pc-build/catalog', access: PC_BUILD_CATALOG_ACCESS, Page: PcBuildCatalogPage },
  { id: 'cart', path: '/cart', access: CART_ACCESS, Page: CartPage },
  /* Оформление — `POST /v1/orders/from-cart`: закрыто правом ручки, а не правом корзины */
  { id: 'checkout', path: '/checkout', access: ORDER_CREATE_ACCESS, Page: CheckoutPage },
]

/**
 * Какой раздел считать текущим. Точное совпадение для корня, префикс — для всего
 * остального: `/orders/14-882` подсвечивает «Заказы».
 */
export function isCurrentSection(to: string, pathname: string): boolean {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}
